import { Box, Grid2 as Grid, Typography, InputAdornment } from "@mui/material";
import { SelectBox, TextField } from "../../common";
import { ChargeInformationRow, ModalForm, WashType } from "../../../types";
import { useValidateForm } from "../../../hooks";
import { adjustmentChargeSchema } from "../../../validations";
import { adjustmentWashTypesDropDownAtom } from "../../../atoms";
import { useAtom } from "jotai";
import { AdjustmentValueType, ChargeType } from "../../../enums";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useState, useEffect } from "react";

type Props = ModalForm<
  ChargeInformationRow,
  {
    washRate: number;
    washTypeId: number;
    valueType: AdjustmentValueType;
    id: number;
  },
  {
    adjustmentTypes: WashType[];
  }
>;

export const AdjustmentForm = ({
  id,
  defaultValues,
  handleEdit,
  adjustmentTypes,
}: Props) => {
  const [adjustmentWashTypesDropDown] = useAtom(
    adjustmentWashTypesDropDownAtom,
  );
  const [valueType, setValueType] = useState<AdjustmentValueType>(
    defaultValues?.valueType ?? AdjustmentValueType.Flat,
  );

  const { register, handleSubmit, errors, setValue } = useValidateForm(
    adjustmentChargeSchema.refine(
      (data) => {
        const selectedAdjustment = adjustmentTypes?.find(
          (item) => item.id === data.washTypeId,
        );

        const isDiscount =
          selectedAdjustment?.chargeType === ChargeType.LocationDiscount;

        return !(
          isDiscount &&
          data.valueType === AdjustmentValueType.Percentage &&
          Number(data.washRate) > 100
        );
      },
      {
        message: "Discount cannot be more than 100%",
        path: ["washRate"],
      },
    ),
  );

  useEffect(() => {
    setValue("valueType", valueType, { shouldValidate: true });
  }, [valueType]);

  const handleEditWrapper = (data: {
    washRate: string;
    washTypeId: number;
  }) => {
    handleEdit({
      washRate: Number(data.washRate),
      washTypeId: data.washTypeId,
      valueType,
      id: defaultValues ? defaultValues.id : 0,
    });
  };

  return (
    <Box
      component="form"
      id={id}
      noValidate
      onSubmit={handleSubmit(handleEditWrapper)}
      sx={{ height: "100%" }}
    >
      <Grid
        container
        size={12}
        direction="column"
        gap={3}
        sx={{
          borderRadius: "10px",
          p: 3,
          mb: 2,
          width: "100%",
        }}
      >
        <Grid size={12}>
          {adjustmentWashTypesDropDown && (
            <SelectBox
              label="Adjustment Type"
              name="washTypeId"
              dropdown={adjustmentWashTypesDropDown}
              register={register}
              error={errors["washTypeId"]}
              defaultValue={defaultValues && defaultValues.id}
              valueAsNumber
            />
          )}
        </Grid>
        <Grid size={12}>
          <Typography>Amount Type</Typography>

          <ToggleButtonGroup
            value={valueType}
            exclusive
            size="small"
            onChange={(_, val) => val !== null && setValueType(val)}
            className="segmented-toggle"
          >
            <ToggleButton value={AdjustmentValueType.Flat}>$</ToggleButton>
            <ToggleButton value={AdjustmentValueType.Percentage}>
              %
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        <Grid size={12}>
          <TextField
            type="number"
            label="Adjustment Rate"
            name="washRate"
            register={register}
            error={errors["washRate"]}
            defaultValue={defaultValues && defaultValues.washRate}
            slotProps={{
              input: {
                startAdornment:
                  valueType === AdjustmentValueType.Flat ? (
                    <InputAdornment position="start">$</InputAdornment>
                  ) : null,
                endAdornment:
                  valueType === AdjustmentValueType.Percentage ? (
                    <InputAdornment position="end">%</InputAdornment>
                  ) : null,
              },
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
